/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: speedDialsApi.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    API client functions for Speed Dials module.
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';

export interface SpeedDial {
  id: number;
  uuid: string;
  domainId: number;
  speedCode: string;
  destination: string;
  description?: string | null;
  classOfServicesId?: number | null;
  classOfService?: {
    id: number;
    name: string;
  };
  enabled: boolean;
  createdAt: string;
  createdBy?: number | null;
  updatedAt?: string | null;
  updatedBy?: number | null;
}

export interface SpeedDialsListResponse {
  success: boolean;
  data: SpeedDial[];
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface SpeedDialResponse {
  success: boolean;
  data: SpeedDial;
  message?: string;
}

export interface ImportSpeedDialsResponse {
  success: boolean;
  data: {
    total: number;
    created: number;
    updated: number;
    skipped: number;
    errors: string[];
  };
  message?: string;
}

/**
 * Get all speed dials with pagination and filtering
 */
export async function getSpeedDials(params: {
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  search?: string;
  domainId?: number;
}): Promise<SpeedDialsListResponse> {
  const queryParams = new URLSearchParams({
    page: String(params.page),
    limit: String(params.limit),
    sortBy: params.sortBy,
    sortOrder: params.sortOrder
  });

  if (params.search?.trim()) {
    queryParams.set('search', params.search.trim());
  }

  // Add domain filter if provided
  if (params.domainId) {
    queryParams.set('domainId', String(params.domainId));
  }

  const response = await fetch(`${buildApiUrl('/api/telephony/speed-dials')}?${queryParams.toString()}`, {
    headers: getApiHeaders()
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  return await response.json();
}

/**
 * Get single speed dial by ID
 */
export async function getSpeedDialById(
  id: string | number
): Promise<SpeedDialResponse> {
  const response = await fetch(buildApiUrl(`/api/telephony/speed-dials/${id}`), {
    headers: getApiHeaders()
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  return await response.json();
}

/**
 * Create new speed dial
 */
export async function createSpeedDial(
  data: Partial<SpeedDial>
): Promise<SpeedDialResponse> {
  const response = await fetch(buildApiUrl('/api/telephony/speed-dials'), {
    method: 'POST',
    headers: {
      ...getApiHeaders(),
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || `HTTP ${response.status}`);
  }

  return await response.json();
}

/**
 * Update speed dial
 */
export async function updateSpeedDial(
  id: string | number,
  data: Partial<SpeedDial>
): Promise<SpeedDialResponse> {
  const response = await fetch(buildApiUrl(`/api/telephony/speed-dials/${id}`), {
    method: 'PUT',
    headers: {
      ...getApiHeaders(),
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || `HTTP ${response.status}`);
  }

  return await response.json();
}

/**
 * Delete speed dial
 */
export async function deleteSpeedDial(
  id: string | number
): Promise<void> {
  const response = await fetch(buildApiUrl(`/api/telephony/speed-dials/${id}`), {
    method: 'DELETE',
    headers: getApiHeaders()
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || `HTTP ${response.status}`);
  }
}

/**
 * Export speed dials to CSV
 */
export async function exportSpeedDials(): Promise<Blob> {
  const response = await fetch(buildApiUrl('/api/telephony/speed-dials/export'), {
    headers: getApiHeaders()
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  return await response.blob();
}

/**
 * Import speed dials from CSV
 */
export async function importSpeedDials(
  csvContent: string,
  mode: 'skip' | 'update' | 'replace' = 'skip'
): Promise<ImportSpeedDialsResponse> {
  const response = await fetch(buildApiUrl('/api/telephony/speed-dials/import'), {
    method: 'POST',
    headers: {
      ...getApiHeaders(),
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ csvContent, mode })
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || `HTTP ${response.status}`);
  }

  return await response.json();
}

